/* eslint-disable react/no-unstable-nested-components */
import ContentSectionHeading from "components/company/sections/ContentSectionHeading";
import { Button } from "components/ui/Button";
import classNames from "components/ui/classNames";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import Twemoji from "components/ui/Twemoji";
import isBefore from "date-fns/isBefore";
import { useAmplitude } from "lib/amplitude/Amplitude";
import useSelfie from "lib/hooks/useSelfie";
import { palette } from "lib/miolo";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

interface IRenderContentSummaryProps {
  onToggleCollapsed: (isCollapsed: boolean) => void;
}

interface IRenderContentProps {
  onToggleCollapsed: (isCollapsed: boolean) => void;
}

interface ICollapsibleContentSectionProps {
  title: string;
  primaryColor?: string;
  isExpanded: boolean;
  isNewSection?: boolean | Date;
  isPremiumSection?: boolean;
  className?: string;
  renderSubtitle: () => ReactNode;
  renderContentSummary?: (props: IRenderContentSummaryProps) => ReactNode;
  renderContent: (props: IRenderContentProps) => ReactNode;
  onToggleCollapsed: (isCollapsed: boolean) => void;
}

export default function CollapsibleContentSection({
  title,
  renderSubtitle,
  renderContentSummary,
  renderContent,
  primaryColor = palette.mariner,
  isExpanded,
  isNewSection,
  isPremiumSection,
  onToggleCollapsed,
  className,
}: ICollapsibleContentSectionProps) {
  const selfie = useSelfie();
  const isCollapsed = !isExpanded;
  const subtitle = renderSubtitle();
  const contentSummary = renderContentSummary ? renderContentSummary({ onToggleCollapsed }) : null;

  return (
    <div className="w-full">
      <div
        className={classNames(
          "relative flex flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-sm",
          "xl:flex-row xl:items-center",
          className,
        )}
      >
        {primaryColor && (
          <div
            aria-hidden={true}
            className="absolute -left-0.5 top-4 w-1 rounded"
            style={{
              height: "calc(100% - 32px)",
              backgroundColor: primaryColor,
            }}
          />
        )}

        <div className="flex flex-row items-center justify-between space-x-4 xl:w-full xl:max-w-md">
          <div className="space-y-0 truncate">
            <ContentSectionHeading>{title}</ContentSectionHeading>
            {subtitle}
            {(isNewSection === true || (isNewSection instanceof Date && isBefore(new Date(), isNewSection))) && (
              <div className="block pt-2">
                <NewSectionTag />
              </div>
            )}
            {isPremiumSection && !selfie?.isPremium && (
              <div className="block pt-2 sm:hidden">
                <OnlyPremiumTag />
              </div>
            )}
          </div>

          <div className="block xl:hidden">
            <CollapseExpandButton
              title={title}
              isCollapsed={isCollapsed}
              isPremiumSection={isPremiumSection}
              onToggleCollapsed={onToggleCollapsed}
            />
          </div>
        </div>

        {contentSummary && <div className="mt-4 w-full xl:mt-0">{contentSummary}</div>}

        <div className="ml-auto hidden pl-4 xl:block">
          <div className="flex items-center space-x-4">
            {isPremiumSection && !selfie?.isPremium && (
              <div className="hidden sm:block">
                <OnlyPremiumWithTooltip side="left" />
              </div>
            )}

            <CollapseExpandButton
              title={title}
              isCollapsed={isCollapsed}
              isPremiumSection={isPremiumSection}
              onToggleCollapsed={onToggleCollapsed}
            />
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-8">
          {renderContent({
            onToggleCollapsed,
          })}
        </div>
      )}

      {isExpanded && (
        <div className="pt-6 text-center">
          <Button
            type="button"
            variant="ghost"
            className="relative m-0 p-2 text-sm font-bold tracking-wider text-neutral-700"
            onClick={() => onToggleCollapsed(false)}
          >
            <FormattedMessage defaultMessage="Esconder" id="PPsbyo" />
          </Button>
        </div>
      )}
    </div>
  );
}

function NewSectionTag() {
  return (
    <div className="flex items-center space-x-2">
      <span className="inline-flex rounded-md bg-orange-50 px-2 py-1 text-sm font-bold text-orange-600">
        <FormattedMessage defaultMessage="Novidade" id="yC7RnY" />
      </span>
      <Twemoji emoji="🎊" width={18} height={18} />
    </div>
  );
}

function OnlyPremiumTag() {
  return <span className="inline-flex rounded-md bg-brand/5 px-2 py-1 text-sm font-bold text-brand">Premium</span>;
}

interface IOnlyPremiumWithTooltipProps {
  side: "top" | "right" | "bottom" | "left";
}

function OnlyPremiumWithTooltip({ side }: IOnlyPremiumWithTooltipProps) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild={true}>
          <div>
            <OnlyPremiumTag />
          </div>
        </TooltipTrigger>
        <TooltipContent side={side} sideOffset={12} className="max-w-xs rounded-xl border border-transparent bg-brand">
          <p className="max-w-[280px] p-1 font-normal text-slate-50">
            <FormattedMessage
              defaultMessage="Conteúdo restrito aos usuários <strong>Premium</strong>. <a>Clique aqui</a> e junte-se ao time para ter acesso completo ao site!"
              id="YM85Dy"
              values={{
                strong: (text) => <strong>{text}</strong>,
                a: (text) => (
                  <Link
                    href="/premium"
                    as="/premium"
                    passHref={true}
                    className="font-bold text-white hover:underline"
                    prefetch={false}
                  >
                    {text}
                  </Link>
                ),
              }}
            />
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface ICollapseExpandButtonProps {
  title: string;
  isCollapsed: boolean;
  isPremiumSection?: boolean;
  onToggleCollapsed: (isCollapsed: boolean) => void;
}

function CollapseExpandButton({ title, isPremiumSection, isCollapsed, onToggleCollapsed }: ICollapseExpandButtonProps) {
  const selfie = useSelfie();
  const router = useRouter();
  const amplitude = useAmplitude();
  const ariaLabel = isCollapsed ? "Expandir" : "Recolher";

  if (isPremiumSection && !selfie?.isPremium) {
    return (
      <Link
        href="/premium"
        as="/premium"
        passHref={true}
        prefetch={false}
        aria-label={ariaLabel}
        className={classNames(
          "flex h-10 w-10 items-center justify-center rounded-full xl:h-12 xl:w-12",
          "border border-transparent bg-slate-50 hover:border-slate-300",
        )}
        onClick={() => {
          amplitude.logEvent("Clicked on expand section button", {
            section: title,
          });
        }}
      >
        <ChevronDownIcon className="w-4 text-slate-800" strokeWidth={2} />
      </Link>
    );
  }

  return (
    <button
      aria-label={ariaLabel}
      type="button"
      className={classNames(
        "flex h-10 w-10 items-center justify-center rounded-full xl:h-12 xl:w-12",
        "border border-transparent bg-slate-50 hover:border-slate-300",
      )}
      onClick={() => {
        amplitude.logEvent("Clicked on expand section button", {
          section: title,
        });
        if (isPremiumSection && !selfie?.isPremium) {
          router.push("/premium", "/premium");
          return;
        }
        onToggleCollapsed(!isCollapsed);
      }}
    >
      {isCollapsed ? (
        <ChevronDownIcon className="w-4 text-slate-800" strokeWidth={2} />
      ) : (
        <ChevronUpIcon className="w-4 text-slate-800" strokeWidth={2} />
      )}
    </button>
  );
}
